import { DownOutlined, FormatPainterFilled, UpOutlined } from '@ant-design/icons';
import { Button, Progress, Select, Tooltip } from 'antd';
import { groupBy, isEqual, uniq } from 'lodash';
import { useCallback, useContext, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';

import { DataTableContext } from 'components/DataTable';
import {
  StyledBanner,
  StyledLink,
} from 'components/DataTable/CustomizedDataBanner/CustomizedDataBanner.style';
import { getFilterBy } from 'components/DataTable/paging';
import { type TQueryFilter } from 'components/DataTable/types';
import { resolveFilterBy } from 'components/DataTable/utils';
import { type TTableColumnDef } from 'horizon/types/TableColumnDef';
import {
  type AtlasGqlFilterBy,
  type AtlasGqlGetHorizonDamagesForPlotQueryVariables,
  type AtlasGqlHorizonDamage,
  useGetBladeIdsForTurbineQuery,
  useGetGenericBladeShapeWidthsQuery,
  useGetHorizonDamagesForPlotQuery,
  useGetPropagationsForPlotQuery,
} from 'types/atlas-graphql';
import { useIsInView } from 'utils/hooks';

import { AreaSelectionIcon } from './AreaSelectionIcon';
import { BladeDamagePlot } from './BladeDamagePlot';
import {
  OpenButtonContainer,
  PlotChartsColumn,
  PlotContainer,
  PlotGrid,
  PlotHeader,
  PlotScrollArea,
} from './BladeDamagePlot.style';
import { ColorCodingLegend } from './ColorCodingLegend';
import { PlotStyleOptionsModal } from './PlotStyleOptionsModal/PlotStyleOptionsModal';
import {
  type ColorCodingMap,
  getColorCodingMap,
  getPropagationTypeColorCodingMap,
  getSeverityColorCodingMap,
} from './pointUtils';
import { UndoAreaSelectionIcon } from './UndoAreaSelectionIcon';
import { type PlotBladeSet } from './usePlotBladeSet';
import { getMakeModelListLabel, MAX_PLOT_BLADE_LENGTH_SPREAD_M } from './utils';

const FIRST_PAGE_SIZE = 100;
const PAGE_SIZE = 1500;

export enum BladeSide {
  LeadingEdge = 'Leading Edge',
  PressureSide = 'Pressure Side',
  SuctionSide = 'Suction Side',
  TrailingEdge = 'Trailing Edge',
}
const BLADE_SIDES = Object.values(BladeSide);

export enum ColorCodingAttribute {
  Severity = 'Severity',
  Type = 'Type',
  OpSeverity = 'OpSeverity',
  Subtype = 'Subtype',
  Material = 'Material',
  PropagationType = 'Propagation Type',
}
export type PlotStyleState = {
  modalOpen: boolean;
  colorCodingAttribute: ColorCodingAttribute | null;
  colorCodingAttributeValues?: string[];
};
const plotStyleStateReducer = (
  prevState: PlotStyleState,
  incomingState: PlotStyleState
): PlotStyleState => ({ ...prevState, ...incomingState });

type BladeDamagePlotContainerProps = {
  isOpen: boolean;
  plotEnabled: boolean;
  onToggle: () => void;
  columns: TTableColumnDef[];
  schemaId?: string;
  plotBladeSet: PlotBladeSet;
  bladeAssetId?: string;
  turbineId?: string;
  isSiteDetailsPage: boolean;
  isTurbineDetailsPage: boolean;
  isBladeDetailsPage: boolean;
  // A page that opens the plot by default explains when it cannot. Elsewhere the Plot Damages
  // action's tooltip does.
  openByDefault?: boolean;
  criticalOnly?: boolean;
  // Scope filters the page sets outside the table's column filters
  universalFilter?: TQueryFilter[];
  bladeSides?: BladeSide[];
  // Off beside a non-Damage table, whose filters name no Damage fields.
  followsDamageTable?: boolean;
};

export const BladeDamagePlotContainer: React.FunctionComponent<BladeDamagePlotContainerProps> = ({
  isOpen,
  plotEnabled,
  onToggle,
  columns,
  schemaId,
  plotBladeSet,
  bladeAssetId,
  turbineId,
  isSiteDetailsPage,
  isTurbineDetailsPage,
  isBladeDetailsPage,
  openByDefault = true,
  criticalOnly,
  universalFilter,
  bladeSides,
  followsDamageTable = true,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isAssetDetailsPage = isSiteDetailsPage || isTurbineDetailsPage || isBladeDetailsPage;
  const {
    makeModels,
    makeModelsMissingLength,
    minLength,
    maxLength,
    loading: plotBladeSetLoading,
  } = plotBladeSet;
  const makeModelLabel = getMakeModelListLabel(makeModels);
  const makeModelsMissingLengthLabel =
    getMakeModelListLabel(makeModelsMissingLength) || t('common.unknown', 'Unknown');
  // A set whose Make/Models all share one length has no range to explain
  const showLengthRange = makeModels.length > 1 && minLength !== maxLength;

  /**
   * Set of refs for unsized divs in the scroll container (see returned jsx) used to determine whether
   * each edge of the container is in view. If an edge is not visible (i.e. the container is below the
   * minimum width and scrolled elsewhere), this is used to add box-shadow in the styled-component to
   * make it clear to the user that there is more to see.
   */
  const topRef = useRef<HTMLDivElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const leftRef = useRef<HTMLDivElement>(null);
  const rightRef = useRef<HTMLDivElement>(null);
  const plotContainerRef = useRef<HTMLDivElement>(null);
  const isScrolledToTop: boolean = useIsInView(topRef);
  const isScrolledToBottom: boolean = useIsInView(bottomRef);
  const isScrolledToLeft: boolean = useIsInView(leftRef);
  const isScrolledToRight: boolean = useIsInView(rightRef);

  const filtersRef = useRef<string>();

  const [selectedBladeSides, setSelectedBladeSides] = useState<BladeSide[]>(
    bladeSides ?? BLADE_SIDES
  );
  const [allowedSelectionBladeSides, setAllowedSelectionBladeSides] = useState<BladeSide[]>([]);
  const [plotStyleState, dispatchPlotStyleState] = useReducer<
    (prevState: PlotStyleState, incomingState: Partial<PlotStyleState>) => PlotStyleState
  >(plotStyleStateReducer, {
    modalOpen: false,
    colorCodingAttribute: ColorCodingAttribute.Severity,
  });
  const { colorCodingAttribute, colorCodingAttributeValues } = plotStyleState;
  const colorCodingEnabled = !!colorCodingAttribute;

  const [bannerDismissed, setBannerDismissed] = useState<boolean>(false);
  const [hasBeenOpened, setHasBeenOpened] = useState<boolean>(false);
  useEffect(() => {
    if (isOpen && !hasBeenOpened) {
      setHasBeenOpened(true);
    }
  }, [isOpen, hasBeenOpened]);

  useEffect(() => {
    setSelectedBladeSides(bladeSides ?? BLADE_SIDES);
  }, [bladeSides]);

  // Pull filters from damage table context for querying the same damages here
  const { filteredInfo: tableFilteredInfo, updateFilteredInfo } = useContext(DataTableContext);
  const filteredInfo = followsDamageTable ? tableFilteredInfo : undefined;

  const { data: turbineBladeIdData } = useGetBladeIdsForTurbineQuery({
    variables: {
      filterBy: [
        { key: 'assetTypeNames', values: ['blade'] },
        {
          key: 'childrenOfIds',
          values: [turbineId!],
        },
      ],
    },
    skip: !isTurbineDetailsPage || !turbineId,
  });

  /**
   * When on a site or turbine details page where the plot is enabled and open by default (all
   * blade descendants are of plottable make/models), make and model filters are set directly,
   * rather than coming from the damages table.
   *
   * All other filters match those of the damages table.
   */
  const filterBy = useMemo(
    () =>
      // TQueryFilter types the operator as a plain string
      resolveFilterBy(
        universalFilter,
        getFilterBy({
          filters: {
            ...filteredInfo,
            ...(schemaId ? { schemaId: [schemaId] } : {}),
            ...(bladeAssetId ? { assetId: [bladeAssetId] } : {}),
            ...(isTurbineDetailsPage && turbineBladeIdData?.assetsWithMetadata.items.length
              ? { componentId: turbineBladeIdData.assetsWithMetadata.items.map(({ id }) => id) }
              : {}),
            ...((isSiteDetailsPage || isTurbineDetailsPage) && makeModels.length
              ? {
                  assetMake: uniq(makeModels.map(({ make }) => make).filter(Boolean)),
                  assetModel: uniq(makeModels.map(({ model }) => model).filter(Boolean)),
                }
              : {}),
            ...(criticalOnly ? { critical: ['true'] } : {}),
          },
          columns,
        }).filter(
          (
            f
          ): f is {
            key: any;
            values: any;
            operator: any;
          } => !!f
        )
      ) as AtlasGqlFilterBy[],
    [
      universalFilter,
      filteredInfo,
      columns,
      bladeAssetId,
      isSiteDetailsPage,
      isTurbineDetailsPage,
      turbineBladeIdData,
      makeModels,
      criticalOnly,
      schemaId,
    ]
  );

  /**
   * Store a stringified version of the current filters in a ref (used to prevent
   * conflicts with long-running queries of paginated damage data)
   */
  useEffect(() => {
    filtersRef.current = JSON.stringify(filterBy);
  }, [JSON.stringify(filterBy)]);

  /**
   * The plotted silhouette is the generic blade shape for every make/model; only its length
   * varies, and that comes from the plot's blade set.
   */
  const { data: bladeShapeData } = useGetGenericBladeShapeWidthsQuery();

  // Fetch first PAGE_SIZE damages; see useEffect below for automatic pagination
  const { data, fetchMore, previousData } = useGetHorizonDamagesForPlotQuery({
    variables: {
      input: {},
      filterBy,
      limit: FIRST_PAGE_SIZE,
      offset: 0,
    },
    skip: !isOpen,
  });

  /**
   * Automatically fetch damages in pages of PAGE_SIZE. Then render data as it is loaded,
   * reducing the time until the graph becomes usable.
   */
  useEffect(() => {
    if (
      data?.getHorizonDamages?.items &&
      (data.getHorizonDamages.items.length < (data.getHorizonDamages.totalCount ?? 0) ||
        data.getHorizonDamages.totalCount !== previousData?.getHorizonDamages?.totalCount)
    ) {
      fetchMore({
        variables: {
          limit: PAGE_SIZE,
          offset: data.getHorizonDamages.items.length,
        },
        updateQuery: (previousData, { fetchMoreResult, variables }) => {
          /**
           * If the returned data is from a query that used the same filters as those currently
           * applied to the plot/table, the result is valid and the cache is updated.
           *
           * If filters change while this query is in flight, the below check will fail and the
           * cache for the main query will not be updated.
           *
           * This prevents issues with in flight queries with stale filters incorrectly overwriting
           * the main query results, which can cause cache conflicts and incorrectly rendered or missing
           * damages.
           */
          const filtersMatch =
            JSON.stringify(
              (variables as AtlasGqlGetHorizonDamagesForPlotQueryVariables).filterBy
            ) === filtersRef.current;

          if (filtersMatch) {
            return {
              getHorizonDamages: {
                items: [
                  ...(previousData.getHorizonDamages?.items ?? []),
                  ...(fetchMoreResult.getHorizonDamages?.items ?? []),
                ],
                totalCount: previousData.getHorizonDamages?.totalCount,
              },
            };
          } else {
            return data;
          }
        },
      });
    }
  }, [data, fetchMore, previousData]);

  const [loadingPercent, loading]: [number, boolean] = useMemo(() => {
    const loadingPercent =
      100 *
      (data?.getHorizonDamages?.totalCount === 0
        ? 1
        : !!data?.getHorizonDamages?.totalCount
          ? (data?.getHorizonDamages?.items?.length ?? 0) / data.getHorizonDamages.totalCount
          : 0);
    return [loadingPercent, loadingPercent < 100];
  }, [data?.getHorizonDamages]);

  const { data: propagationData, loading: propagationsLoading } = useGetPropagationsForPlotQuery({
    variables: {
      input: {
        damageIds: data?.getHorizonDamages?.items?.map(d => d?.id) ?? [],
      },
    },
    skip:
      !data?.getHorizonDamages?.items?.length ||
      loadingPercent < 100 ||
      colorCodingAttribute !== ColorCodingAttribute.PropagationType,
  });

  const damagesByBladeSide: { [Property in BladeSide]: AtlasGqlHorizonDamage[] } = useMemo(
    () =>
      groupBy(data?.getHorizonDamages?.items, 'primaryObservationGroupAttrs["Blade Side"]') as {
        [Property in BladeSide]: AtlasGqlHorizonDamage[];
      },
    [data?.getHorizonDamages?.items]
  );

  const colorCodingMap: ColorCodingMap | undefined = useMemo(() => {
    if (
      colorCodingEnabled &&
      ![ColorCodingAttribute.Severity, ColorCodingAttribute.PropagationType].includes(
        colorCodingAttribute
      ) &&
      colorCodingAttributeValues
    ) {
      return Object.fromEntries(
        Object.entries(getColorCodingMap(theme, colorCodingAttributeValues)).map(
          ([attributeValue, { symbol }]) => [
            attributeValue,
            {
              symbol,
              damageCount: data?.getHorizonDamages?.items?.filter(
                d => d?.primaryObservationGroupAttrs[colorCodingAttribute] === attributeValue
              ).length,
            },
          ]
        )
      );
    }
    if (colorCodingEnabled && colorCodingAttribute === ColorCodingAttribute.Severity) {
      const severityColorCodingMap = getSeverityColorCodingMap(theme);
      return Object.fromEntries(
        Object.entries(severityColorCodingMap).map(([severity, { symbol }]) => [
          severity,
          {
            symbol,
            damageCount: data?.getHorizonDamages?.items?.filter(
              d => d?.primaryObservationGroupAttrs[colorCodingAttribute] === Number(severity)
            ).length,
          },
        ])
      );
    }
    if (colorCodingEnabled && colorCodingAttribute === ColorCodingAttribute.PropagationType) {
      // call lazy query to fetch propagations here
      // figure out necessary pagination, merging, etc.

      return Object.fromEntries(
        Object.entries(getPropagationTypeColorCodingMap(theme)).map(
          ([propagationType, { symbol }]) => [
            propagationType,
            {
              symbol,
              damageCount: data?.getHorizonDamages?.items?.filter(d => {
                const primaryGroupPropagationType =
                  propagationData?.getPrimaryGroupPropagationsForDamages?.find(
                    p => p?.damageId === d?.id
                  )?.type;
                return primaryGroupPropagationType === propagationType;
              }).length,
            },
          ]
        )
      );
    }
    return undefined;
  }, [
    data?.getHorizonDamages?.items,
    propagationData?.getPrimaryGroupPropagationsForDamages,
    colorCodingEnabled,
    colorCodingAttribute,
  ]);

  const bladeShapeWidths: number[] = useMemo(
    () => bladeShapeData?.genericShapeData?.crossSectionWidths ?? [],
    [bladeShapeData]
  );

  /**
   * Blades of different lengths are plotted together on a single axis scaled to the longest of
   * them (their lengths are within MAX_PLOT_BLADE_LENGTH_SPREAD_M of each other).
   */
  const bladeLength = maxLength as number;

  // When an area is selected on a plot, sets the blade side, distance, and chord filters on the table and plot accordingly
  const handleAreaSelection = useCallback(
    ({
      bladeSide,
      distanceRange,
      chordRange,
    }: {
      bladeSide: BladeSide | null;
      distanceRange: [number | null, number | null] | null;
      chordRange?: [number, number] | null;
    }) => {
      setAllowedSelectionBladeSides(bladeSide ? [bladeSide] : []);
      updateFilteredInfo({
        ...filteredInfo,
        'Blade Side': bladeSide ? [bladeSide] : null,
        Distance: distanceRange ? [distanceRange] : null,
        Chord: chordRange ? [chordRange] : null,
      });
    },
    [filteredInfo, updateFilteredInfo]
  );

  const areaSelected = allowedSelectionBladeSides.length === 1;

  // TODO: add loading indicator when fetching propagations

  return (
    <>
      {plotEnabled && hasBeenOpened && (
        <OpenButtonContainer>
          <Button
            icon={isOpen ? <DownOutlined /> : <UpOutlined />}
            onClick={onToggle}
            title={isOpen ? t('damage.hidePlot', 'Hide plot') : t('damage.showPlot', 'Show plot')}
          />
        </OpenButtonContainer>
      )}
      {isAssetDetailsPage &&
        openByDefault &&
        !plotEnabled &&
        !plotBladeSetLoading &&
        !bannerDismissed && (
          <StyledBanner $gridGap="0.5rem">
            {makeModelsMissingLength.length
              ? t(
                  'damage.unableToPlotMissingLengthBanner',
                  'Unable to plot Damages because length data is missing for: {{makeModels}}.',
                  { makeModels: makeModelsMissingLengthLabel }
                )
              : t(
                  'damage.unableToPlotBanner',
                  'Unable to plot Damages due to inconsistent or missing Blade Make & Model data. Filter the table to Blade Make & Models with lengths within {{spread}}m of each other to enable plot.',
                  { spread: MAX_PLOT_BLADE_LENGTH_SPREAD_M }
                )}
            <StyledLink type="link" onClick={() => setBannerDismissed(true)}>
              {t('common.close', 'Close')}
            </StyledLink>
          </StyledBanner>
        )}
      {(isSiteDetailsPage || isTurbineDetailsPage) && plotEnabled && isOpen && !bannerDismissed && (
        <StyledBanner $gridGap="0.5rem">
          <span>
            {isSiteDetailsPage ? (
              <Trans
                i18nKey="damage.plotBannerSite"
                t={t}
                values={{ makeModels: makeModelLabel }}
                components={{ strong: <strong /> }}
                defaults="This plot shows all Damages on <strong>{{makeModels}} Blades</strong> at this Site that match all other filters applied to the table below. Damages on other Assets at this Site may be present in the table."
              />
            ) : (
              <Trans
                i18nKey="damage.plotBannerTurbine"
                t={t}
                values={{ makeModels: makeModelLabel }}
                components={{ strong: <strong /> }}
                defaults="This plot shows all Damages on <strong>{{makeModels}} Blades</strong> on this Turbine that match all other filters applied to the table below. Damages on other components on this Turbine may be present in the table."
              />
            )}{' '}
            {showLengthRange &&
              t(
                'damage.plotBannerLengthRange',
                'Blade lengths range from {{minLength}}m to {{maxLength}}m; the plot is scaled to the longest.',
                { minLength, maxLength }
              )}
          </span>
          <StyledLink type="link" onClick={() => setBannerDismissed(true)}>
            {t('common.close', 'Close')}
          </StyledLink>
        </StyledBanner>
      )}
      {!isAssetDetailsPage &&
        plotEnabled &&
        isOpen &&
        makeModels.length > 1 &&
        !bannerDismissed && (
          <StyledBanner $gridGap="0.5rem">
            <span>
              {followsDamageTable ? (
                <Trans
                  i18nKey="damage.plotBannerMakeModels"
                  t={t}
                  values={{ makeModels: makeModelLabel }}
                  components={{ strong: <strong /> }}
                  defaults="This plot shows all Damages on <strong>{{makeModels}} Blades</strong> that match the filters applied to the table below."
                />
              ) : (
                <Trans
                  i18nKey="damage.plotBannerPlottedMakeModels"
                  t={t}
                  values={{ makeModels: makeModelLabel }}
                  components={{ strong: <strong /> }}
                  defaults="This plot shows all Damages on <strong>{{makeModels}} Blades</strong>."
                />
              )}{' '}
              {showLengthRange &&
                t(
                  'damage.plotBannerLengthRange',
                  'Blade lengths range from {{minLength}}m to {{maxLength}}m; the plot is scaled to the longest.',
                  { minLength, maxLength }
                )}
            </span>
            <StyledLink type="link" onClick={() => setBannerDismissed(true)}>
              {t('common.close', 'Close')}
            </StyledLink>
          </StyledBanner>
        )}
      {isBladeDetailsPage && plotEnabled && isOpen && !bannerDismissed && (
        <StyledBanner $gridGap="0.5rem">
          <span>
            {t(
              'damage.plotBannerBlade',
              'This plot shows all Damages on this Blade that match all other filters applied to the table below.'
            )}
          </span>
          <StyledLink type="link" onClick={() => setBannerDismissed(true)}>
            {t('common.close', 'Close')}
          </StyledLink>
        </StyledBanner>
      )}
      <PlotContainer
        ref={plotContainerRef}
        className={isOpen ? 'open' : ''}
        $isScrolledToBottom={isScrolledToBottom}
        $isScrolledToLeft={isScrolledToLeft}
        $isScrolledToRight={isScrolledToRight}
      >
        <PlotHeader $isScrolledToTop={isScrolledToTop}>
          <div>
            <span>{t('damage.bladeSideLabel', 'Blade Side:')}</span>
            <Select
              options={BLADE_SIDES.map(side => ({
                value: side,
                disabled: isEqual(selectedBladeSides, [side]),
              }))}
              value={selectedBladeSides}
              onChange={sides =>
                setSelectedBladeSides(BLADE_SIDES.filter(side => sides.includes(side)))
              }
              showSearch={true}
              mode="multiple"
            />
            {(loading || propagationsLoading) && (
              <Tooltip
                title={`${
                  data?.getHorizonDamages?.totalCount && loading
                    ? t('damage.damagesLoaded', '{{loaded}}/{{total}} damages loaded', {
                        loaded: data?.getHorizonDamages?.items?.length ?? 0,
                        total: data.getHorizonDamages.totalCount,
                      })
                    : ''
                }${colorCodingAttribute === ColorCodingAttribute.PropagationType ? `${loading ? ', ' : ''}${t('damage.propagationTypesLoading', 'Propagation types loading')}` : ''}`}
              >
                <Progress
                  style={{
                    cursor: 'pointer',
                    marginLeft: '1rem',
                    width: '200px',
                  }}
                  strokeColor={theme.teal}
                  percent={loading ? loadingPercent : 80}
                  showInfo={false}
                />
              </Tooltip>
            )}
          </div>

          <div>
            {followsDamageTable && (
              <Button
                icon={areaSelected ? <UndoAreaSelectionIcon /> : <AreaSelectionIcon />}
                onClick={() => {
                  if (areaSelected) {
                    handleAreaSelection({
                      bladeSide: null,
                      distanceRange: null,
                      chordRange: null,
                    });
                  } else {
                    setAllowedSelectionBladeSides(BLADE_SIDES);
                  }
                }}
                title={t('damage.selectAreaToFilter', 'Select an area to filter by')}
              />
            )}
            <Button
              className={colorCodingEnabled ? 'active' : ''}
              icon={<FormatPainterFilled />}
              onClick={() => dispatchPlotStyleState({ modalOpen: true })}
              title={t('damage.colorCodeDamages', 'Color code damages by attribute')}
            />
          </div>
        </PlotHeader>
        <PlotScrollArea>
          <div ref={topRef} />
          <PlotGrid $isScrolledToLeft={isScrolledToLeft} $isScrolledToRight={isScrolledToRight}>
            <div ref={leftRef} />
            <PlotChartsColumn>
              {plotEnabled &&
                BLADE_SIDES.map(bladeSide => (
                  <BladeDamagePlot
                    key={bladeSide}
                    visible={selectedBladeSides.includes(bladeSide)}
                    bladeSide={bladeSide}
                    damages={damagesByBladeSide[bladeSide] as AtlasGqlHorizonDamage[]}
                    propagations={propagationData?.getPrimaryGroupPropagationsForDamages ?? []}
                    damageColumns={columns}
                    loading={loading}
                    shapeWidths={bladeShapeWidths}
                    bladeLength={bladeLength}
                    colorCodingAttribute={colorCodingEnabled ? colorCodingAttribute : undefined}
                    colorCodingMap={colorCodingMap}
                    allowAreaSelection={allowedSelectionBladeSides.includes(bladeSide)}
                    onAreaSelection={handleAreaSelection}
                  />
                ))}
            </PlotChartsColumn>
            <div ref={rightRef} />
          </PlotGrid>
          <div ref={bottomRef} />
        </PlotScrollArea>
        {plotEnabled && isOpen && colorCodingEnabled && colorCodingMap && (
          <ColorCodingLegend
            colorCodingAttribute={colorCodingAttribute}
            colorCodingMap={colorCodingMap}
            totalDamageCount={data?.getHorizonDamages?.items?.length ?? 0}
            boundsRef={plotContainerRef}
          />
        )}
      </PlotContainer>
      <PlotStyleOptionsModal
        plotStyleState={plotStyleState}
        dispatchPlotStyleState={dispatchPlotStyleState}
        schemaId={schemaId}
      />
    </>
  );
};
